import { sendAnalytics, type DeviceHandoffMethod } from '../../../../telemetry/client';
import type { DeviceLockTarget } from '../../types/theme';
import type { HandoffTarget } from '../../types/deviceFallbackTheme';
import type { BaseAnalyticsProps } from '../IQuickstartTelemetryManager';

/**
 * Cross-device handoff funnel.
 *
 * `Device Fallback Viewed` fires on the blocked device, `Device Handoff Completed` on the
 * device the visitor moves to. Both carry the same persistentId — bridged via the URL —
 * so the two halves can be joined downstream.
 */

// Mirrors how DeviceFallbackScreen picks its component, so the reported variant is always
// the screen the visitor actually saw. A campaign with no playable platform gets the
// unavailable screen, which offers no QR and no email to report as a handoff path.
const variantFor = (
  detectedDeviceType: 'ios' | 'android' | 'desktop',
  handoffTarget: HandoffTarget,
): 'desktop_qr' | 'mobile_email' | 'unavailable' => {
  if (handoffTarget === 'none') return 'unavailable';
  return detectedDeviceType === 'desktop' ? 'desktop_qr' : 'mobile_email';
};

export function sendDeviceFallbackViewedEvent(
  baseProps: BaseAnalyticsProps,
  playerAuthId: string | undefined,
  detectedDeviceType: 'ios' | 'android' | 'desktop',
  lockedTo: DeviceLockTarget,
  handoffTarget: HandoffTarget,
): void {
  sendAnalytics({
    name: 'Device Fallback Viewed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    detectedDeviceType,
    lockedTo,
    variant: variantFor(detectedDeviceType, handoffTarget),
  });
}

export function sendDeviceHandoffRequestedEvent(
  baseProps: BaseAnalyticsProps,
  playerAuthId: string | undefined,
  handoffMethod: DeviceHandoffMethod,
  detectedDeviceType: 'ios' | 'android' | 'desktop',
): void {
  sendAnalytics({
    name: 'Device Handoff Requested',
    role: 'player',
    ...baseProps,
    playerAuthId,
    handoffMethod,
    detectedDeviceType,
  });
}

export function sendDeviceHandoffCompletedEvent(
  baseProps: BaseAnalyticsProps,
  playerAuthId: string | undefined,
  handoffMethod: DeviceHandoffMethod,
  arrivedDeviceType: 'ios' | 'android' | 'desktop',
  originDeviceType?: 'ios' | 'android' | 'desktop',
): void {
  sendAnalytics({
    name: 'Device Handoff Completed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    handoffMethod,
    arrivedDeviceType,
    originDeviceType,
  });
}
